/**
 * Geolocation Mock Utility for Development/Testing
 * 
 * This utility allows testing navigation functionality when not physically in Amsterdam.
 * 
 * Usage:
 * 1. In browser console: window.enableGeolocationMock([52.355, 4.795])
 * 2. Or set localStorage: localStorage.setItem('mock-geolocation', '52.355,4.795')
 * 3. To disable: window.disableGeolocationMock() or remove from localStorage
 */

import { OSDORP_CENTER } from '../config/osdorpArea';

export interface MockGeolocationConfig {
  enabled: boolean;
  position: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  simulateMovement?: boolean; // Simulate walking along a route
  movementSpeed?: number; // meters per second (default: 1.4 m/s ≈ 5 km/h walking speed)
}

class GeolocationMockService {
  private mockConfig: MockGeolocationConfig | null = null;
  private watchInterval: NodeJS.Timeout | null = null;
  private currentMockPosition: [number, number] | null = null;
  private movementTarget: [number, number] | null = null;
  private movementStartTime: number = 0;
  private routeCoordinates: [number, number][] | null = null;
  private currentRouteIndex: number = 0;
  private isSimulating: boolean = false;
  private watchCallbacks: PositionCallback[] = [];

  /**
   * Initialize mock geolocation from localStorage or environment
   */
  initialize(): void {
    // Check if mock is enabled via localStorage
    const mockLocation = localStorage.getItem('mock-geolocation');
    if (mockLocation) {
      const [lat, lng] = mockLocation.split(',').map(Number);
      if (!isNaN(lat) && !isNaN(lng)) {
        this.enable([lat, lng]);
      }
    }

    // Expose global functions for easy testing
    if (typeof window !== 'undefined') {
      (window as any).enableGeolocationMock = (position?: [number, number]) => {
        // Use default Osdorp-Midden center if no position provided
        const mockPosition: [number, number] = position || OSDORP_CENTER;
        this.enable(mockPosition);
        localStorage.setItem('mock-geolocation', `${mockPosition[0]},${mockPosition[1]}`);
        console.log('✅ Geolocation mock enabled at:', mockPosition);
        if (!position) {
          console.log('💡 Using default Osdorp-Midden center. Provide coordinates to use a custom location.');
        }
      };

      (window as any).disableGeolocationMock = () => {
        this.disable();
        localStorage.removeItem('mock-geolocation');
        console.log('❌ Geolocation mock disabled');
      };

      (window as any).setMockPosition = (position: [number, number]) => {
        if (this.mockConfig) {
          this.currentMockPosition = position;
          this.mockConfig.position.latitude = position[0];
          this.mockConfig.position.longitude = position[1];
          this.notifyAllCallbacks();
          console.log('📍 Mock position updated to:', position);
        } else {
          console.warn('⚠️ Mock not enabled. Call enableGeolocationMock() first.');
        }
      };

      (window as any).simulateRouteWalk = (route: [number, number][], speed?: number) => {
        if (!this.isEnabled()) {
          console.warn('⚠️ Mock not enabled. Call enableGeolocationMock() first.');
          return;
        }
        this.startRouteSimulation(route, speed);
        console.log(`🚶 Started simulating walk along route with ${route.length} points`);
      };

      (window as any).stopRouteSimulation = () => {
        this.stopRouteSimulation();
        console.log('⏹️ Route simulation stopped');
      };

      (window as any).pauseRouteSimulation = () => {
        this.pauseRouteSimulation();
        console.log('⏸️ Route simulation paused');
      };

      (window as any).resumeRouteSimulation = () => {
        this.resumeRouteSimulation();
        console.log('▶️ Route simulation resumed');
      };

      (window as any).setSimulationSpeed = (speed: number) => {
        if (this.mockConfig) {
          this.mockConfig.movementSpeed = speed;
          console.log(`⚡ Simulation speed set to ${speed} m/s (${(speed * 3.6).toFixed(1)} km/h)`);
        }
      };

      (window as any).resetRouteSimulation = (route?: [number, number][]) => {
        this.resetRouteSimulation(route);
        if (route) {
          console.log('🔄 Route simulation reset to start of new route');
        } else {
          console.log('🔄 Route simulation reset');
        }
      };
    }
  }

  /**
   * Enable geolocation mock with a specific position
   */
  enable(position: [number, number], simulateMovement: boolean = false): void {
    this.mockConfig = {
      enabled: true,
      position: {
        latitude: position[0],
        longitude: position[1],
        accuracy: 5, // 5 meters accuracy
      },
      simulateMovement,
      movementSpeed: 1.4, // ~5 km/h walking speed
    };
    this.currentMockPosition = position;
  }

  /**
   * Disable geolocation mock
   */
  disable(): void {
    this.stopRouteSimulation();
    this.mockConfig = null;
    this.currentMockPosition = null;
    this.movementTarget = null;
    this.routeCoordinates = null;
    this.currentRouteIndex = 0;
    this.watchCallbacks = [];
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
      this.watchInterval = null;
    }
  }

  /**
   * Check if mock is enabled
   */
  isEnabled(): boolean {
    return this.mockConfig?.enabled === true;
  }

  /**
   * Get current mock position
   */
  getMockPosition(): [number, number] | null {
    return this.currentMockPosition;
  }

  /**
   * Override navigator.geolocation methods
   * 
   * This override intercepts geolocation calls and:
   * - Returns mock positions when mock is enabled
   * - Falls back to real GPS when mock is disabled
   * 
   * This ensures real GPS works correctly when mock is not enabled.
   */
  overrideGeolocation(): void {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return;
    }

    const originalWatchPosition = navigator.geolocation.watchPosition.bind(navigator.geolocation);
    const originalGetCurrentPosition = navigator.geolocation.getCurrentPosition.bind(navigator.geolocation);

    // Override watchPosition
    navigator.geolocation.watchPosition = (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback,
      options?: PositionOptions
    ): number => {
      if (this.isEnabled() && this.mockConfig) {
        // Return mock position
        const mockPosition: GeolocationPosition = {
          coords: {
            latitude: this.mockConfig.position.latitude,
            longitude: this.mockConfig.position.longitude,
            accuracy: this.mockConfig.position.accuracy || 5,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          },
          timestamp: Date.now(),
        } as GeolocationPosition;

        // Store callback for route simulation
        this.watchCallbacks.push(successCallback);

        // Call success callback immediately
        successCallback(mockPosition);

        // If simulating route, start the simulation
        if (this.isSimulating && this.routeCoordinates && this.routeCoordinates.length > 0) {
          // Simulation will be handled by the route simulation interval
        } else {
          // Otherwise, just update periodically with same position (for consistency)
          const intervalId = setInterval(() => {
            if (this.isEnabled() && this.mockConfig && !this.isSimulating) {
              const updatedPosition: GeolocationPosition = {
                ...mockPosition,
                coords: {
                  ...mockPosition.coords,
                  latitude: this.mockConfig.position.latitude,
                  longitude: this.mockConfig.position.longitude,
                },
                timestamp: Date.now(),
              } as GeolocationPosition;
              this.notifyAllCallbacks(updatedPosition);
            } else {
              clearInterval(intervalId);
            }
          }, 1000); // Update every second

          // Return a watch ID (we'll use interval ID)
          return intervalId as unknown as number;
        }

        return 1; // Return a mock watch ID
      }

      // Fall back to real geolocation if mock is disabled
      return originalWatchPosition(successCallback, errorCallback, options);
    };

    // Override getCurrentPosition
    navigator.geolocation.getCurrentPosition = (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback,
      options?: PositionOptions
    ): void => {
      if (this.isEnabled() && this.mockConfig) {
        const mockPosition: GeolocationPosition = {
          coords: {
            latitude: this.mockConfig.position.latitude,
            longitude: this.mockConfig.position.longitude,
            accuracy: this.mockConfig.position.accuracy || 5,
            altitude: null,
            altitudeAccuracy: null,
            heading: null,
            speed: null,
          },
          timestamp: Date.now(),
        } as GeolocationPosition;
        successCallback(mockPosition);
        return;
      }

      // Fall back to real geolocation
      originalGetCurrentPosition(successCallback, errorCallback, options);
    };
  }

  /**
   * Simulate movement along a route
   */
  simulateMovementTo(target: [number, number]): void {
    if (!this.currentMockPosition) return;

    this.movementTarget = target;
    this.movementStartTime = Date.now();
    this.mockConfig = {
      ...this.mockConfig!,
      simulateMovement: true,
    };
  }
  /**
   * Start simulating movement along a route
   */
  startRouteSimulation(route: [number, number][], speed?: number): void {
    if (route.length === 0) {
      console.warn('⚠️ Route is empty');
      return;
    }

    this.routeCoordinates = route;
    this.currentRouteIndex = 0;
    this.isSimulating = true;

    // Set initial position to first route point
    if (this.mockConfig) {
      this.currentMockPosition = route[0];
      this.mockConfig.position.latitude = route[0][0];
      this.mockConfig.position.longitude = route[0][1];
      if (speed) {
        this.mockConfig.movementSpeed = speed;
      }
    }

    // Start the simulation interval
    this.startRouteSimulationInterval();
  }

  /**
   * Stop route simulation
   */
  stopRouteSimulation(): void {
    this.isSimulating = false;
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
      this.watchInterval = null;
    }
    this.routeCoordinates = null;
    this.currentRouteIndex = 0;
  }

  /**
   * Pause route simulation
   */
  pauseRouteSimulation(): void {
    this.isSimulating = false;
    if (this.watchInterval) {
      clearInterval(this.watchInterval);
      this.watchInterval = null;
    }
  }

  /**
   * Resume route simulation
   */
  resumeRouteSimulation(): void {
    if (this.routeCoordinates && this.routeCoordinates.length > 0) {
      this.isSimulating = true;
      this.startRouteSimulationInterval();
    }
  }

  /**
   * Reset route simulation to the start of a route (or current route)
   */
  resetRouteSimulation(route?: [number, number][]): void {
    // Stop any ongoing simulation
    this.stopRouteSimulation();

    // If a new route is provided, use it; otherwise keep current route
    if (route && route.length > 0) {
      this.routeCoordinates = route;
    }

    // Reset position to start of route
    if (this.routeCoordinates && this.routeCoordinates.length > 0 && this.mockConfig) {
      const startPosition = this.routeCoordinates[0];
      this.currentMockPosition = startPosition;
      this.currentRouteIndex = 0;
      this.mockConfig.position.latitude = startPosition[0];
      this.mockConfig.position.longitude = startPosition[1];
      
      // Notify all callbacks of the reset position
      this.notifyAllCallbacks();
    }
  }

  /**
   * Start the interval that moves along the route
   */
  private startRouteSimulationInterval(): void {
    if (!this.routeCoordinates || this.routeCoordinates.length === 0 || !this.mockConfig) {
      return;
    }

    const updateInterval = 200; // Update every 200ms for smooth movement

    let accumulatedDistance = 0;
    let currentSegmentIndex = 0;

    this.watchInterval = setInterval(() => {
      if (!this.isSimulating || !this.routeCoordinates || !this.mockConfig) {
        return;
      }

      // Read the current speed on every tick so that changes made via
      // window.setSimulationSpeed() take effect immediately.
      const currentSpeed = this.mockConfig.movementSpeed || 1.4; // m/s
      const distancePerUpdate = (currentSpeed * updateInterval) / 1000; // meters per update

      // Move to next segment if we've passed the current one
      while (currentSegmentIndex < this.routeCoordinates.length - 1) {
        const [currentLat, currentLng] = this.routeCoordinates[currentSegmentIndex];
        const [nextLat, nextLng] = this.routeCoordinates[currentSegmentIndex + 1];
        
        const segmentDistance = this.calculateDistance(currentLat, currentLng, nextLat, nextLng);
        
        if (accumulatedDistance + distancePerUpdate >= segmentDistance) {
          // Move to next segment
          accumulatedDistance = 0;
          currentSegmentIndex++;
          this.currentRouteIndex = currentSegmentIndex;
          
          if (currentSegmentIndex >= this.routeCoordinates.length - 1) {
            // Reached end of route
            this.currentMockPosition = this.routeCoordinates[this.routeCoordinates.length - 1];
            this.mockConfig.position.latitude = this.currentMockPosition[0];
            this.mockConfig.position.longitude = this.currentMockPosition[1];
            this.notifyAllCallbacks();
            this.stopRouteSimulation();
            console.log('🏁 Route simulation completed - reached destination');
            return;
          }
        } else {
          break;
        }
      }

      // Calculate position along current segment
      if (currentSegmentIndex < this.routeCoordinates.length - 1) {
        const [currentLat, currentLng] = this.routeCoordinates[currentSegmentIndex];
        const [nextLat, nextLng] = this.routeCoordinates[currentSegmentIndex + 1];
        
        const segmentDistance = this.calculateDistance(currentLat, currentLng, nextLat, nextLng);
        const progress = Math.min((accumulatedDistance + distancePerUpdate) / segmentDistance, 1);
        
        const newLat = currentLat + (nextLat - currentLat) * progress;
        const newLng = currentLng + (nextLng - currentLng) * progress;
        
        this.currentMockPosition = [newLat, newLng];
        this.mockConfig.position.latitude = newLat;
        this.mockConfig.position.longitude = newLng;
        this.currentRouteIndex = currentSegmentIndex;
        
        accumulatedDistance += distancePerUpdate;
        
        this.notifyAllCallbacks();
      }
    }, updateInterval);
  }

  /**
   * Notify all registered watch callbacks
   */
  private notifyAllCallbacks(position?: GeolocationPosition): void {
    if (!this.mockConfig) return;

    const mockPosition: GeolocationPosition = position || {
      coords: {
        latitude: this.mockConfig.position.latitude,
        longitude: this.mockConfig.position.longitude,
        accuracy: this.mockConfig.position.accuracy || 5,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: this.mockConfig.movementSpeed || null,
      },
      timestamp: Date.now(),
    } as GeolocationPosition;

    this.watchCallbacks.forEach(callback => {
      try {
        callback(mockPosition);
      } catch (error) {
        console.error('Error in watch callback:', error);
      }
    });
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }
}

// Singleton instance
export const geolocationMock = new GeolocationMockService();

// Auto-initialize (available in both development and production)
if (typeof window !== 'undefined') {
  geolocationMock.initialize();
  geolocationMock.overrideGeolocation();
  
  // Log instructions in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('%c🧪 Geolocation Mock Available', 'color: #4CAF50; font-weight: bold; font-size: 14px;');
    console.log('%cBasic Commands:', 'color: #2196F3; font-weight: bold;');
    console.log('  window.enableGeolocationMock() // Uses default Osdorp-Midden center');
    console.log('  window.enableGeolocationMock([52.355, 4.795]) // Custom position');
    console.log('  window.setMockPosition([52.355, 4.795])');
    console.log('  window.disableGeolocationMock()');
    console.log('%cRoute Simulation (for NavigationMode):', 'color: #9C27B0; font-weight: bold;');
    console.log('  window.simulateRouteWalk([[lat1, lng1], [lat2, lng2], ...], speed?)');
    console.log('  window.stopRouteSimulation()');
    console.log('  window.pauseRouteSimulation()');
    console.log('  window.resumeRouteSimulation()');
    console.log('  window.setSimulationSpeed(1.4) // m/s (default: 1.4 m/s ≈ 5 km/h)');
    console.log('%c💡 Tip: In NavigationMode, get route coordinates from the component props!', 'color: #FF9800; font-style: italic;');
  }
}
